/*
 * NavList messages
 *
 * Every user-facing string on the NAV page lives here so it goes through react-intl, the same
 * mechanism the sidebar uses. Keys are mirrored in src/translations/en.js and src/translations/th.js;
 * react-intl falls back to defaultMessage when a key is missing from a locale file.
 */
import { defineMessages } from "react-intl";

export default defineMessages({
  // ----- Tabs -----
  tabNavHistory: {
    id: "app.containers.NavList.tabNavHistory",
    defaultMessage: "NAV History"
  },
  tabTopHolding: {
    id: "app.containers.NavList.tabTopHolding",
    defaultMessage: "Top Holding"
  },
  tabSyncStatus: {
    id: "app.containers.NavList.tabSyncStatus",
    defaultMessage: "Sync Status"
  },
  tabLatestByFund: {
    id: "app.containers.NavList.tabLatestByFund",
    defaultMessage: "Latest by fund"
  },

  // ----- Filter bar -----
  filterFund: {
    id: "app.containers.NavList.filterFund",
    defaultMessage: "Fund"
  },
  filterFundPlaceholder: {
    id: "app.containers.NavList.filterFundPlaceholder",
    defaultMessage: "All funds"
  },
  filterFundLoading: {
    id: "app.containers.NavList.filterFundLoading",
    defaultMessage: "Loading funds..."
  },
  filterFundCode: {
    id: "app.containers.NavList.filterFundCode",
    defaultMessage: "Fund code"
  },
  filterFundCodePlaceholder: {
    id: "app.containers.NavList.filterFundCodePlaceholder",
    defaultMessage: "Search by fund code"
  },
  filterNavDate: {
    id: "app.containers.NavList.filterNavDate",
    defaultMessage: "NAV date"
  },
  filterValueDate: {
    id: "app.containers.NavList.filterValueDate",
    defaultMessage: "Value date"
  },
  filterSourceTypeNav: {
    id: "app.containers.NavList.filterSourceTypeNav",
    defaultMessage: "Source type NAV"
  },
  filterSyncBy: {
    id: "app.containers.NavList.filterSyncBy",
    defaultMessage: "Synced by"
  },
  filterRowsPerPage: {
    id: "app.containers.NavList.filterRowsPerPage",
    defaultMessage: "Rows per page"
  },
  filterAnyPlaceholder: {
    id: "app.containers.NavList.filterAnyPlaceholder",
    defaultMessage: "Any"
  },
  buttonSearch: {
    id: "app.containers.NavList.buttonSearch",
    defaultMessage: "Search"
  },
  buttonClear: {
    id: "app.containers.NavList.buttonClear",
    defaultMessage: "Clear filters"
  },
  buttonRetry: {
    id: "app.containers.NavList.buttonRetry",
    defaultMessage: "Try again"
  },

  // ----- Status blocks -----
  loadingNavHistory: {
    id: "app.containers.NavList.loadingNavHistory",
    defaultMessage: "Retrieving NAV history..."
  },
  loadingTopHolding: {
    id: "app.containers.NavList.loadingTopHolding",
    defaultMessage: "Retrieving top holdings..."
  },
  loadingSyncStatus: {
    id: "app.containers.NavList.loadingSyncStatus",
    defaultMessage: "Retrieving sync status..."
  },
  emptyHeader: {
    id: "app.containers.NavList.emptyHeader",
    defaultMessage: "No result found"
  },
  emptyBody: {
    id: "app.containers.NavList.emptyBody",
    defaultMessage: "No synced row matches these filters. Try widening the date range or clearing the fund filter."
  },
  errorHeader: {
    id: "app.containers.NavList.errorHeader",
    defaultMessage: "Could not load the synced data"
  },
  errorBodyWithDetail: {
    id: "app.containers.NavList.errorBodyWithDetail",
    defaultMessage: "The request failed ({detail}). Nothing below is up to date."
  },
  errorBodyNetwork: {
    id: "app.containers.NavList.errorBodyNetwork",
    defaultMessage: "The request did not reach the server. Nothing below is up to date."
  },
  errorStatus: {
    id: "app.containers.NavList.errorStatus",
    defaultMessage: "HTTP {status}"
  },

  // ----- Pagination / counters -----
  totalRows: {
    id: "app.containers.NavList.totalRows",
    defaultMessage: "{total} rows"
  },
  pageOfTotal: {
    id: "app.containers.NavList.pageOfTotal",
    defaultMessage: "Page {page} of {pages}"
  },
  goToPage: {
    id: "app.containers.NavList.goToPage",
    defaultMessage: "Go to page"
  },
  goToPageButton: {
    id: "app.containers.NavList.goToPageButton",
    defaultMessage: "Go"
  },

  // ----- Shared columns -----
  colFundNo: {
    id: "app.containers.NavList.colFundNo",
    defaultMessage: "FUND NO."
  },
  colFundCode: {
    id: "app.containers.NavList.colFundCode",
    defaultMessage: "FUND CODE"
  },
  colFundName: {
    id: "app.containers.NavList.colFundName",
    defaultMessage: "FUND NAME"
  },
  colSyncBy: {
    id: "app.containers.NavList.colSyncBy",
    defaultMessage: "SYNC BY"
  },
  colCreatedAt: {
    id: "app.containers.NavList.colCreatedAt",
    defaultMessage: "CREATED AT"
  },
  colUpdatedAt: {
    id: "app.containers.NavList.colUpdatedAt",
    defaultMessage: "UPDATED AT"
  },

  // ----- NAV history columns -----
  colNavDate: {
    id: "app.containers.NavList.colNavDate",
    defaultMessage: "NAV DATE"
  },
  colNavPerUnit: {
    id: "app.containers.NavList.colNavPerUnit",
    defaultMessage: "NAV PER UNIT"
  },
  colPriceChg: {
    id: "app.containers.NavList.colPriceChg",
    defaultMessage: "PRICE CHG"
  },
  colPercentChg: {
    id: "app.containers.NavList.colPercentChg",
    defaultMessage: "PERCENT CHG"
  },
  colReturnType: {
    id: "app.containers.NavList.colReturnType",
    defaultMessage: "RETURN TYPE"
  },
  colNavEffdateRaw: {
    id: "app.containers.NavList.colNavEffdateRaw",
    defaultMessage: "NAV EFFDATE RAW"
  },
  colSourceTypeNav: {
    id: "app.containers.NavList.colSourceTypeNav",
    defaultMessage: "SOURCE TYPE NAV"
  },

  // ----- Top holding columns -----
  colSeq: {
    id: "app.containers.NavList.colSeq",
    defaultMessage: "SEQ"
  },
  colListTh: {
    id: "app.containers.NavList.colListTh",
    defaultMessage: "LIST (TH)"
  },
  colListEng: {
    id: "app.containers.NavList.colListEng",
    defaultMessage: "LIST (EN)"
  },
  colAmountInv: {
    id: "app.containers.NavList.colAmountInv",
    defaultMessage: "AMOUNT INV"
  },
  colPercentInv: {
    id: "app.containers.NavList.colPercentInv",
    defaultMessage: "PERCENT INV"
  },
  colPercentInvRaw: {
    id: "app.containers.NavList.colPercentInvRaw",
    defaultMessage: "PERCENT INV RAW"
  },
  colValueDate: {
    id: "app.containers.NavList.colValueDate",
    defaultMessage: "VALUE DATE"
  },

  // ----- Sync state columns -----
  colJobName: {
    id: "app.containers.NavList.colJobName",
    defaultMessage: "JOB NAME"
  },
  colLastRunAt: {
    id: "app.containers.NavList.colLastRunAt",
    defaultMessage: "LAST RUN AT"
  },
  colLastSuccessAt: {
    id: "app.containers.NavList.colLastSuccessAt",
    defaultMessage: "LAST SUCCESS AT"
  },
  colLastStatus: {
    id: "app.containers.NavList.colLastStatus",
    defaultMessage: "LAST STATUS"
  },
  colRowsAffected: {
    id: "app.containers.NavList.colRowsAffected",
    defaultMessage: "ROWS AFFECTED"
  },
  colDetail: {
    id: "app.containers.NavList.colDetail",
    defaultMessage: "DETAIL"
  },

  // ----- Coverage summary columns -----
  colRowCount: {
    id: "app.containers.NavList.colRowCount",
    defaultMessage: "ROW COUNT"
  },
  colEarliestNavDate: {
    id: "app.containers.NavList.colEarliestNavDate",
    defaultMessage: "EARLIEST NAV DATE"
  },
  colLatestNavDate: {
    id: "app.containers.NavList.colLatestNavDate",
    defaultMessage: "LATEST NAV DATE"
  },
  colLatestNavPerUnit: {
    id: "app.containers.NavList.colLatestNavPerUnit",
    defaultMessage: "LATEST NAV PER UNIT"
  },
  colLastSyncBy: {
    id: "app.containers.NavList.colLastSyncBy",
    defaultMessage: "LAST SYNC BY"
  },

  // ----- Latest by fund -----
  colLatestPriceChg: {
    id: "app.containers.NavList.colLatestPriceChg",
    defaultMessage: "PRICE CHG"
  },
  colLatestPercentChg: {
    id: "app.containers.NavList.colLatestPercentChg",
    defaultMessage: "PERCENT CHG"
  },
  colLatestReturnType: {
    id: "app.containers.NavList.colLatestReturnType",
    defaultMessage: "RETURN TYPE"
  },
  colLatestSourceTypeNav: {
    id: "app.containers.NavList.colLatestSourceTypeNav",
    defaultMessage: "SOURCE TYPE NAV"
  },
  latestByFundCaption: {
    id: "app.containers.NavList.latestByFundCaption",
    defaultMessage:
      "One row per fund: the newest NAV this dashboard holds for it. {funds} funds"
  },

  // ----- Sync status page -----
  sectionSyncJobs: {
    id: "app.containers.NavList.sectionSyncJobs",
    defaultMessage: "Sync jobs"
  },
  sectionCoverage: {
    id: "app.containers.NavList.sectionCoverage",
    defaultMessage: "Coverage per fund"
  },
  sectionMissingFunds: {
    id: "app.containers.NavList.sectionMissingFunds",
    defaultMessage: "Funds with no NAV history"
  },
  missingFundsBody: {
    id: "app.containers.NavList.missingFundsBody",
    defaultMessage: "{count} of {total} funds in the fund master have no row in funds_nav_history at all."
  },
  missingFundsNone: {
    id: "app.containers.NavList.missingFundsNone",
    defaultMessage: "Every fund in the fund master has NAV history. {total} funds covered."
  },
  missingFundsUnknown: {
    id: "app.containers.NavList.missingFundsUnknown",
    defaultMessage: "The fund master could not be read, so coverage gaps cannot be listed."
  },
  coverageSummaryCount: {
    id: "app.containers.NavList.coverageSummaryCount",
    defaultMessage: "{funds} funds, {rows} NAV rows in total"
  },
  badgeNoData: {
    id: "app.containers.NavList.badgeNoData",
    defaultMessage: "NO DATA"
  },
  refresh: {
    id: "app.containers.NavList.refresh",
    defaultMessage: "Refresh"
  }
});
